/**
 * src/shell/portal/components/MobileReelsFeed.tsx
 *
 * TikTok / Instagram Reels Mobile Feed:
 *  - Full-bleed vertical swipe reel (h-[100dvh] snap-y snap-mandatory)
 *  - Autoplay muted default with single-tap audio toggle & animated volume badge
 *  - First-time user "Skip Intro" overlay with auto-dismiss
 *  - Right-rail interaction dock:
 *      * Creator avatar with LIVE indicator & instant vouch
 *      * Kinetic like with haptic vibration (navigator.vibrate)
 *      * Slide-up comments drawer with real-time channel messages
 *      * Pinned Creator Garage drop with 1-click escrow checkout
 *  - Watch-time analytics reporting to /api/v1/analytics/reel-view
 *  - Parental Safe-Mode content filtering (Kids/Teens/Family Safe)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface ReelItem {
  id: string;
  creatorHandle: string;
  creatorDisplayName?: string;
  creatorAvatarUrl?: string;
  caption: string;
  channelName: string;
  videoUrl: string;
  posterUrl: string;
  likesCount: number;
  commentsCount: number;
  isMature?: boolean;
  pinnedProduct?: {
    id: string;
    title: string;
    price: string;
    seller?: string;
  };
}

export interface MobileReelsFeedProps {
  initialReels?: ReelItem[];
  userSafeMode?: 'STANDARD' | 'STRICT_FAMILY' | 'KIDS_ENCLAVE';
  onProductCheckout?: (productId: string) => void;
  onExitReels?: () => void;
  onSelectChannel?: (channelName: string) => void;
}

const DEFAULT_REELS: ReelItem[] = [
  {
    id: 'reel_esp32_relay',
    creatorHandle: '@hercules',
    creatorDisplayName: 'Hercules Sovereign',
    creatorAvatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=hercules',
    caption: 'ESP32-S3 battery bridge with WebRTC fanout across edge nodes ⚡ #node-runners #mesh',
    channelName: '#tech-depin',
    videoUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    posterUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerBlazes.jpg',
    likesCount: 1420,
    commentsCount: 382,
    pinnedProduct: {
      id: 'drop_esp32_bridge_enclosure',
      title: 'ESP32 Mesh Gateway Enclosure',
      price: '$1.50 USD (15 TKN)',
      seller: 'Hardware Guild',
    },
  },
  {
    id: 'reel_synth_kl',
    creatorHandle: '@nadiachen',
    creatorDisplayName: 'Nadia Chen',
    creatorAvatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=nadiachen',
    caption: 'Live lo-fi ambient generative audio session from Kuala Lumpur studio 🎵 #ambient #lofi',
    channelName: '#creators-media',
    videoUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    posterUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerEscapes.jpg',
    likesCount: 940,
    commentsCount: 165,
    pinnedProduct: {
      id: 'drop_lofi_synth_pack',
      title: 'Analog Lo-Fi Synth & Sample Pack',
      price: '$25 USD (250 TKN)',
      seller: 'Nadia Audio',
    },
  },
  {
    id: 'reel_mesh_drone',
    creatorHandle: '@tariq_mesh',
    creatorDisplayName: 'Tariq Al-Mansoor',
    creatorAvatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=tariq',
    caption: 'Autonomous drone flight telemetry synchronized over delay-tolerant gossip nodes 🛸',
    channelName: '#tech-depin',
    videoUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    posterUrl: 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerFun.jpg',
    likesCount: 2840,
    commentsCount: 610,
    pinnedProduct: {
      id: 'drop_depin_consulting_slot',
      title: '1-on-1 Mesh Architecture Session',
      price: '$120 USD (1200 TKN)',
      seller: 'Tariq Al-Mansoor',
    },
  },
];

export const MobileReelsFeed: React.FC<MobileReelsFeedProps> = ({
  initialReels = DEFAULT_REELS,
  userSafeMode = 'STANDARD',
  onProductCheckout,
  onExitReels,
  onSelectChannel,
}) => {
  const [reels] = useState<ReelItem[]>(() => {
    if (userSafeMode === 'STRICT_FAMILY' || userSafeMode === 'KIDS_ENCLAVE') {
      return initialReels.filter((r) => !r.isMature);
    }
    return initialReels;
  });

  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [likedReels, setLikedReels] = useState<Record<string, boolean>>({});
  const [activeCommentsReelId, setActiveCommentsReelId] = useState<string | null>(null);
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [commentsMap, setCommentsMap] = useState<Record<string, string[]>>({
    reel_esp32_relay: [
      '⚡ Awesome latency numbers on the ESP32 bridge!',
      'Can we flash this over the WebUSB tool?',
    ],
    reel_synth_kl: [
      'Love the ambient pads! Just grabbed the sample pack.',
      'Sounding so warm and clean 🔥',
    ],
  });

  // First-time intro onboarding overlay
  const [showIntro, setShowIntro] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem('herx_reels_intro_seen');
  });

  const [hiddenReelIds, setHiddenReelIds] = useState<Set<string>>(new Set());
  const [shareToast, setShareToast] = useState<string | null>(null);

  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const viewStartTimes = useRef<Record<string, number>>({});

  const showToast = (msg: string) => {
    setShareToast(msg);
    setTimeout(() => setShareToast(null), 2500);
  };

  const handleNotInterested = (reelId: string) => {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([20, 50, 20]);
    }
    setHiddenReelIds((prev) => new Set([...prev, reelId]));
    showToast('Reel hidden. We will tune your recommendations.');

    fetch('/api/v1/analytics/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reelId, action: 'NOT_INTERESTED' }),
    }).catch(() => {});
  };

  const handleShare = async (reel: ReelItem) => {
    const shareData = {
      title: `${reel.creatorHandle} on HERX Reels`,
      text: reel.caption,
      url: window.location.href,
    };

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        showToast('Shared successfully!');
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          showToast('Share link ready');
        }
      }
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(window.location.href);
      showToast('Link copied to clipboard! 🔗');
    }

    fetch('/api/v1/analytics/reel-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reelId: reel.id, shared: true }),
    }).catch(() => {});
  };

  useEffect(() => {
    if (showIntro) {
      const timer = setTimeout(() => {
        setShowIntro(false);
        localStorage.setItem('herx_reels_intro_seen', 'true');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showIntro]);

  // Track watch time per reel
  const sendReelAnalytics = useCallback(
    (reelId: string, durationMs: number, completed: boolean) => {
      try {
        fetch('/api/v1/analytics/reel-view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reelId,
            viewDuration: durationMs,
            completed,
            liked: Boolean(likedReels[reelId]),
          }),
        }).catch(() => {});
      } catch {}
    },
    [likedReels]
  );

  const handleVideoPlay = (reelId: string) => {
    viewStartTimes.current[reelId] = Date.now();
  };

  const handleVideoEnded = (reelId: string) => {
    const start = viewStartTimes.current[reelId] || Date.now();
    const duration = Date.now() - start;
    sendReelAnalytics(reelId, duration, true);
  };

  const toggleLike = (reelId: string) => {
    // Kinetic vibration feedback on mobile
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(40);
    }
    setLikedReels((prev) => ({ ...prev, [reelId]: !prev[reelId] }));
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  const handleAddComment = (reelId: string) => {
    const text = (commentInputs[reelId] || '').trim();
    if (!text) return;
    setCommentsMap((prev) => ({
      ...prev,
      [reelId]: [...(prev[reelId] || []), text],
    }));
    setCommentInputs((prev) => ({ ...prev, [reelId]: '' }));
  };

  return (
    <div
      data-testid="mobile-reels-feed"
      className="h-[100dvh] w-full overflow-y-scroll snap-y snap-mandatory bg-black text-white select-none relative font-sans"
      style={{ scrollBehavior: 'smooth' }}
    >
      {/* First-Time User Interactive Intro Overlay */}
      {showIntro && (
        <div
          onClick={() => {
            setShowIntro(false);
            localStorage.setItem('herx_reels_intro_seen', 'true');
          }}
          className="absolute inset-0 z-50 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center animate-fadeIn cursor-pointer"
        >
          <div className="w-16 h-16 rounded-3xl bg-indigo-600 flex items-center justify-center text-3xl mb-4 shadow-lg shadow-indigo-500/40">
            🎬
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white mb-2 font-mono">
            HERX REELS
          </h2>
          <p className="text-sm text-gray-300 max-w-xs mb-6">
            Swipe up to discover live WebRTC streams, creator drops, and decentralized audio.
          </p>
          <div className="flex items-center gap-4 text-xs font-semibold text-indigo-400">
            <span>👆 Swipe Up</span>
            <span>•</span>
            <span>🔊 Tap Sound</span>
            <span>•</span>
            <span>❤️ Double Tap</span>
          </div>
          <button
            type="button"
            className="mt-8 px-6 py-2.5 rounded-full bg-white text-black font-bold text-xs hover:bg-gray-200 transition"
          >
            Start Watching
          </button>
        </div>
      )}

      {/* Toast Notification */}
      {shareToast && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-indigo-600/90 backdrop-blur-md text-white font-bold text-xs shadow-2xl border border-indigo-400/40 animate-fadeIn pointer-events-none">
          {shareToast}
        </div>
      )}

      {/* Exit Button / Header */}
      <div className="absolute top-4 left-4 z-40 flex items-center gap-2">
        {onExitReels && (
          <button
            type="button"
            onClick={onExitReels}
            className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-xs font-semibold text-white flex items-center gap-1.5 hover:bg-black/80 transition"
          >
            <span>← Exit Feed</span>
          </button>
        )}
      </div>

      {reels.filter((r) => !hiddenReelIds.has(r.id)).map((reel) => (
        <div
          key={reel.id}
          className="relative h-[100dvh] w-full snap-start flex items-center justify-center overflow-hidden bg-[#090b10]"
        >
          {/* Full-Bleed Video Element */}
          <video
            ref={(el) => (videoRefs.current[reel.id] = el)}
            src={reel.videoUrl}
            poster={reel.posterUrl}
            playsInline
            muted={isMuted}
            autoPlay
            loop
            onPlay={() => handleVideoPlay(reel.id)}
            onEnded={() => handleVideoEnded(reel.id)}
            onClick={toggleMute}
            className="absolute inset-0 w-full h-full object-cover cursor-pointer"
          />

          {/* Dark gradient scrims for readability */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/80" />

          {/* Top Bar Overlay */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-30 pointer-events-none">
            <button
              type="button"
              onClick={() => onSelectChannel?.(reel.channelName)}
              className="pointer-events-auto px-3 py-1 rounded-full bg-black/60 backdrop-blur-md text-xs font-mono font-semibold border border-white/10 text-indigo-300 hover:text-white hover:border-indigo-400/50 hover:bg-indigo-950/40 transition flex items-center gap-1.5 shadow-lg group"
              title={`Jump to ${reel.channelName} living room`}
            >
              <span className="text-[10px] text-gray-400 group-hover:text-indigo-300">in</span>
              <span className="font-bold">{reel.channelName}</span>
              <span className="text-[10px] text-indigo-400 group-hover:translate-x-0.5 transition-transform">→</span>
            </button>
            <button
              type="button"
              onClick={toggleMute}
              className="pointer-events-auto p-2 rounded-full bg-black/60 backdrop-blur-md text-white text-xs border border-white/10 hover:bg-black/80 transition"
            >
              {isMuted ? '🔇 Muted' : '🔊 Sound On'}
            </button>
          </div>

          {/* Right Action Rail */}
          <div className="absolute right-3 bottom-24 flex flex-col items-center gap-4 z-30">
            {/* Host Profile Avatar */}
            <div className="relative w-12 h-12 rounded-full border-2 border-indigo-500 overflow-hidden bg-gray-900 shadow-lg">
              <img
                src={reel.creatorAvatarUrl || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + reel.id}
                alt={reel.creatorHandle}
                className="w-full h-full object-cover"
              />
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] bg-red-600 text-white px-1.5 rounded-full font-black tracking-wider uppercase">
                LIVE
              </span>
            </div>

            {/* Kinetic Like */}
            <button
              type="button"
              onClick={() => toggleLike(reel.id)}
              className="flex flex-col items-center text-white group"
            >
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md transition-transform active:scale-125 ${
                  likedReels[reel.id]
                    ? 'bg-rose-600/90 text-white shadow-lg shadow-rose-600/40'
                    : 'bg-black/50 border border-white/15'
                }`}
              >
                <span className="text-lg">❤️</span>
              </div>
              <span className="text-[11px] font-bold mt-1 drop-shadow">
                {reel.likesCount + (likedReels[reel.id] ? 1 : 0)}
              </span>
            </button>

            {/* Comments Drawer Trigger */}
            <button
              type="button"
              onClick={() => setActiveCommentsReelId(reel.id)}
              className="flex flex-col items-center text-white"
            >
              <div className="w-11 h-11 rounded-full bg-black/50 backdrop-blur-md border border-white/15 flex items-center justify-center hover:bg-black/70 transition">
                <span className="text-lg">💬</span>
              </div>
              <span className="text-[11px] font-bold mt-1 drop-shadow">
                {reel.commentsCount + (commentsMap[reel.id]?.length || 0)}
              </span>
            </button>

            {/* Share Action */}
            <button
              type="button"
              onClick={() => handleShare(reel)}
              className="flex flex-col items-center text-white group"
              title="Share Reel"
            >
              <div className="w-11 h-11 rounded-full bg-black/50 backdrop-blur-md border border-white/15 flex items-center justify-center hover:bg-black/70 transition">
                <span className="text-lg">🔗</span>
              </div>
              <span className="text-[11px] font-bold mt-1 drop-shadow">Share</span>
            </button>

            {/* Not Interested Signal */}
            <button
              type="button"
              onClick={() => handleNotInterested(reel.id)}
              className="flex flex-col items-center text-gray-400 hover:text-rose-400 transition"
              title="Not Interested"
            >
              <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-rose-950/40 hover:border-rose-500/30 transition">
                <span className="text-base">🚫</span>
              </div>
              <span className="text-[10px] font-semibold mt-1 drop-shadow">Hide</span>
            </button>

            {/* Pinned Creator Garage Shop Drop */}
            {reel.pinnedProduct && (
              <button
                type="button"
                onClick={() => onProductCheckout?.(reel.pinnedProduct!.id)}
                className="flex flex-col items-center text-white animate-bounce"
              >
                <div className="w-11 h-11 rounded-full bg-amber-500/90 backdrop-blur-md border border-amber-300 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <span className="text-lg">🛍️</span>
                </div>
                <span className="text-[10px] font-black text-amber-300 mt-1 uppercase tracking-wider drop-shadow">
                  Shop
                </span>
              </button>
            )}
          </div>

          {/* Bottom Information Overlay */}
          <div className="absolute left-4 right-16 bottom-16 z-30 pointer-events-none">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-bold text-white drop-shadow-md">
                {reel.creatorDisplayName || reel.creatorHandle}
              </h4>
              <span className="text-xs text-indigo-400 font-mono drop-shadow">
                {reel.creatorHandle}
              </span>
              <button
                type="button"
                onClick={() => onSelectChannel?.(reel.channelName)}
                className="pointer-events-auto px-2 py-0.5 rounded-full bg-white/10 hover:bg-indigo-500/20 text-[10px] text-indigo-300 border border-white/10 hover:border-indigo-400/40 font-mono transition"
              >
                in {reel.channelName}
              </button>
            </div>
            <p className="text-xs text-gray-200 line-clamp-2 drop-shadow leading-relaxed">
              {reel.caption}
            </p>

            {/* Pinned Product Pill */}
            {reel.pinnedProduct && (
              <div
                onClick={() => onProductCheckout?.(reel.pinnedProduct!.id)}
                className="pointer-events-auto mt-2.5 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/80 backdrop-blur-md border border-amber-500/40 text-xs shadow-lg cursor-pointer hover:bg-black/90 transition"
              >
                <span className="text-amber-400 font-bold">{reel.pinnedProduct.title}</span>
                <span className="text-white font-mono text-[11px]">{reel.pinnedProduct.price}</span>
                <span className="text-[10px] text-amber-300 bg-amber-500/20 px-1.5 py-0.5 rounded font-bold uppercase">
                  1-Click Escrow
                </span>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Slide-Up Comments Drawer */}
      {activeCommentsReelId && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col justify-end animate-fadeIn">
          <div className="w-full max-w-md mx-auto bg-[#10141e] border-t border-white/10 rounded-t-3xl p-5 text-white max-h-[60vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
              <h3 className="text-sm font-bold">Comments</h3>
              <button
                type="button"
                onClick={() => setActiveCommentsReelId(null)}
                className="text-gray-400 hover:text-white p-1"
              >
                ✕
              </button>
            </div>

            {/* Comment List */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {(commentsMap[activeCommentsReelId] || []).map((msg, i) => (
                <div key={i} className="text-xs bg-white/5 rounded-xl p-2.5 border border-white/5">
                  <span className="text-indigo-400 font-mono font-semibold mr-1.5">@mesh_user:</span>
                  <span className="text-gray-200">{msg}</span>
                </div>
              ))}
              {(!commentsMap[activeCommentsReelId] || commentsMap[activeCommentsReelId].length === 0) && (
                <p className="text-xs text-gray-500 text-center py-6">
                  No comments yet. Start the conversation!
                </p>
              )}
            </div>

            {/* Comment Input */}
            <div className="pt-3 flex gap-2">
              <input
                type="text"
                placeholder="Add a comment..."
                value={commentInputs[activeCommentsReelId] || ''}
                onChange={(e) =>
                  setCommentInputs((prev) => ({ ...prev, [activeCommentsReelId]: e.target.value }))
                }
                onKeyDown={(e) => e.key === 'Enter' && handleAddComment(activeCommentsReelId)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-indigo-500"
              />
              <button
                type="button"
                onClick={() => handleAddComment(activeCommentsReelId)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 font-bold rounded-xl text-xs transition"
              >
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
